import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ScrollText, FileDown, CalendarClock, Clock, MapPin, ListChecks, HandHeart } from "lucide-react";
import { ShareButton, guideSharePayload } from "@/components/share/ShareButton";

/**
 * Static reference page — "Assemblea straordinaria: come richiederne la convocazione".
 * Content is intentionally embedded (no CMS / DB entry) so nothing is added
 * or overwritten in the database.
 * Reading-optimised for smartphones: single-column, clear steps, prominent
 * download CTA aligned with the visual language of RipartizioneSpese.jsx.
 */

const MODULO_URL =
    "https://www.nowgestioni.it/wp-content/uploads/2025/09/Richiesta-Convocazione-Assemblea-ex-art.-66-disp.-Att.-C.C-1.doc";

const AVVISO_ITEMS = [
    { icon: CalendarClock, label: "Data dell'assemblea" },
    { icon: Clock, label: "Ora della riunione" },
    { icon: MapPin, label: "Luogo di svolgimento" },
    { icon: ListChecks, label: "Ordine del giorno" },
];

export default function AssembleaStraordinaria() {
    return (
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 text-muted-foreground" data-testid="assemblea-back">
                <Link to="/faq"><ArrowLeft className="size-4 mr-1" /> Torna alle Domande veloci</Link>
            </Button>

            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ScrollText className="size-3.5" /> Guida rapida
            </div>
            <h1
                className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight"
                data-testid="assemblea-title"
            >
                L'amministratore non convoca l'assemblea:<br className="hidden sm:block" /> come richiedere un'assemblea straordinaria?
            </h1>
            <p className="mt-3 text-slate-600 leading-relaxed" data-testid="assemblea-subtitle">
                Scopri quando i condomini possono richiedere la convocazione di un'assemblea straordinaria e come procedere se l'amministratore non provvede.
            </p>

            {/* Discreet peer-help share strip — under the title */}
            <div
                data-testid="guide-share-block"
                className="mt-5 rounded-xl border border-primary/15 bg-primary/[0.04] px-4 sm:px-5 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            >
                <div className="flex items-start sm:items-center gap-2.5 flex-1 min-w-0">
                    <HandHeart className="size-4 text-accent shrink-0 mt-0.5 sm:mt-0" strokeWidth={2.4} />
                    <p className="text-[13.5px] sm:text-sm text-slate-700 leading-snug">
                        <span className="font-semibold text-slate-900">Aiuta chi ne ha bisogno.</span>{" "}
                        Conosci qualcuno con lo stesso dubbio? Condividi questa guida.
                    </p>
                </div>
                <ShareButton
                    payload={guideSharePayload({
                        path: "/faq/assemblea-straordinaria",
                        title: "Come richiedere un'assemblea straordinaria — CondominioNow",
                    })}
                    variant="compact"
                    label="Condividi"
                    data-testid="guide-share-assemblea"
                    className="shrink-0"
                />
            </div>

            {/* Contenuto principale */}
            <div className="mt-8 space-y-5 text-[15px] sm:text-base text-slate-800 leading-relaxed" data-testid="assemblea-content">
                <p>
                    Ai sensi dell'<strong>art. 66 delle disposizioni di attuazione del Codice Civile</strong>, almeno <strong>due condomini che rappresentino un sesto del valore dell'edificio</strong> possono richiedere all'amministratore la convocazione di un'assemblea straordinaria.
                </p>
                <p>
                    La richiesta deve essere <strong>formulata per iscritto</strong> e deve indicare in modo chiaro <strong>gli argomenti da inserire all'ordine del giorno</strong>.
                </p>
                <p>
                    Se l'amministratore non provvede alla convocazione entro <strong>dieci giorni dalla ricezione della richiesta</strong>, i condomini richiedenti possono procedere direttamente all'<strong>autoconvocazione dell'assemblea</strong>.
                </p>
                <p>
                    In tal caso, l'avviso di convocazione dovrà essere inviato a tutti gli aventi diritto rispettando le medesime modalità e gli stessi termini previsti dalla legge per la convocazione delle assemblee condominiali.
                </p>
            </div>

            {/* Cosa deve indicare l'avviso — visual list */}
            <section className="mt-8" data-testid="assemblea-avviso">
                <h2 className="font-serif text-xl sm:text-2xl font-semibold text-slate-900 mb-3 leading-tight">
                    L'avviso dovrà indicare:
                </h2>
                <ul className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                    {AVVISO_ITEMS.map(({ icon: Icon, label }, i) => (
                        <li key={i} className="flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4">
                            <span className="inline-flex items-center justify-center shrink-0 size-9 rounded-full bg-primary/10 text-primary border border-primary/25">
                                <Icon className="size-4" strokeWidth={2.2} />
                            </span>
                            <span className="text-[15px] sm:text-base text-slate-800 leading-snug">{label}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Sezione Download */}
            <section
                data-testid="assemblea-download"
                className="mt-10 rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-white to-amber-50/60 p-5 sm:p-6"
            >
                <div className="flex items-start gap-4">
                    <span className="inline-flex items-center justify-center shrink-0 size-12 rounded-2xl bg-primary/10 text-primary border border-primary/25">
                        <FileDown className="size-6" strokeWidth={2.2} />
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="text-xs uppercase tracking-widest text-primary font-semibold">Risorsa scaricabile</div>
                        <h3 className="mt-0.5 font-serif text-lg sm:text-xl font-semibold text-slate-900 leading-tight">
                            Hai bisogno del modello?
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                            Per facilitare la richiesta di convocazione dell'assemblea straordinaria, puoi utilizzare il modulo predisposto da Nowgestioni.
                        </p>
                        <div className="mt-4 flex justify-center sm:justify-start">
                            <Button
                                asChild
                                size="lg"
                                className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full"
                                data-testid="assemblea-download-btn"
                            >
                                <a href={MODULO_URL} target="_blank" rel="noopener noreferrer">
                                    <FileDown className="size-4 mr-2" strokeWidth={2.4} />
                                    Scarica il modulo di richiesta
                                </a>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer note */}
            <p className="mt-10 text-xs text-muted-foreground leading-relaxed border-t border-slate-200 pt-5">
                <strong>Nota</strong>: questa guida ha valore informativo e non sostituisce il parere di un professionista qualificato. Per casi specifici, invia una domanda al nostro esperto.
            </p>

            {/* CTA */}
            <div className="mt-8 rounded-2xl bg-slate-950 text-white p-6 sm:p-8 text-center">
                <h3 className="font-serif text-2xl font-semibold">Un dubbio sulla tua assemblea?</h3>
                <p className="mt-2 text-slate-300 text-sm">Ogni situazione può avere regole particolari. Chiedi al nostro esperto.</p>
                <Button asChild size="lg" className="mt-5 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full">
                    <Link to="/registrati">Fai una domanda</Link>
                </Button>
            </div>
        </div>
    );
}
